import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Scale, CheckCircle2, AlertTriangle, Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import { CajaCard } from "./CajaCard";
import { DiaReporte } from "./DiaReporte";
import { Reprogramaciones } from "./Reprogramaciones";
import { Q, hoyGT } from "./util";

export const CobrosCiudad = () => {
  const [fecha, setFecha] = useState(hoyGT());
  const [d, setD] = useState(null);
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    api.get("/mi-dinero/ciudad", { params: { fecha } }).then(({ data }) => { if (n === seq.current) setD(data); }).catch((e) => toast.error(formatApiError(e)));
  }, [fecha]);
  useEffect(() => { load(); }, [load]);
  const cerrar = async () => {
    if (!window.confirm(`¿Cerrar el día ${fecha}? Las cajitas quedarán bloqueadas.`)) return;
    try { await api.post("/mi-dinero/ciudad/cierre", { fecha }); toast.success("Día cerrado"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!d) return <div className="text-sm text-zinc-500 p-6">Cargando…</div>;
  const t = d.totales;
  const cuadra = Math.abs(t.diferencia) < 0.01 && d.cajas.every((c) => c.verificado !== null || c.sistema === 0);
  const hoy = d.dias.find((x) => x.fecha === fecha);
  return (
    <div className="space-y-3" data-testid="cobros-ciudad">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="ciudad-fecha" />
        {d.cerrado
          ? <span className="inline-flex items-center gap-1 text-sm font-bold text-zinc-700"><Lock className="w-4 h-4" />Día cerrado</span>
          : <Button onClick={cerrar} className="bg-violet-700 hover:bg-violet-800 ml-auto" data-testid="ciudad-cierre-diario"><Lock className="w-4 h-4 mr-1" />Cierre diario</Button>}
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {d.cajas.map((c, i) => <CajaCard key={`${fecha}-${c.caja}`} c={c} idx={i} fecha={fecha} cerrado={d.cerrado} onSaved={load} />)}
        {!d.cajas.length && <div className="text-sm text-zinc-500" data-testid="ciudad-sin-bancos">No hay bancos o formas de pago activos. Agréguelos en «Bancos o Formas de Pago».</div>}
      </div>
      <div className="grid md:grid-cols-4 gap-3 items-stretch">
        <div className="md:col-span-3 rounded-2xl border bg-white flex flex-wrap items-center justify-around gap-3 p-3 text-center">
          <div><div className="text-xs">Disponible en sistema</div><b className="text-2xl" data-testid="ciudad-tot-sistema"><Q v={t.sistema} /></b></div>
          <div><div className="text-xs">Total verificado</div><b className="text-2xl" data-testid="ciudad-tot-verificado"><Q v={t.verificado} /></b></div>
          <div><div className="text-xs flex items-center gap-1 justify-center"><Scale className="w-4 h-4" />Diferencia total</div><b className={`text-2xl ${t.diferencia ? "text-red-600" : ""}`} data-testid="ciudad-tot-dif"><Q v={t.diferencia} /></b></div>
        </div>
        <div className={`rounded-2xl p-3 flex items-center gap-2 text-white font-black ${cuadra ? "bg-emerald-600" : "bg-amber-500"}`} data-testid="ciudad-conciliacion">
          {cuadra ? <CheckCircle2 className="w-8 h-8" /> : <AlertTriangle className="w-8 h-8" />}
          <div><div className="text-xs font-semibold">Conciliación del día</div>{cuadra ? "CUADRADA" : "PENDIENTE"}</div>
        </div>
      </div>
      <div className="rounded-2xl bg-violet-700 text-white px-4 py-3 flex items-center gap-3">
        <Truck className="w-7 h-7" />
        <div><div className="font-black text-lg">Reportes de App Domicilios</div><div className="text-xs opacity-90">Entregas, cobros, verificación bancaria y facturación</div></div>
      </div>
      {!hoy && <div className="text-sm text-zinc-500 px-2" data-testid="ciudad-sin-cobros">Sin cobros registrados el {fecha}.</div>}
      <Reprogramaciones />
      {d.dias.map((x) => <DiaReporte key={x.fecha} d={x} abierto={x.fecha === fecha} onChanged={load} />)}
    </div>
  );
};
